import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import type { ResetPasswordRequest } from '@medtrack/shared';

import { api, ApiClientError } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { Button } from '@/components/ui/button';
import { InlineError } from '@/components/ui/error-state';
import { AuthLayout } from './AuthLayout';
import { PasswordField, PASSWORD_MIN_LENGTH } from './PasswordField';

/**
 * Read the token out of the URL fragment. PURE: no side effects.
 *
 * The emailed link is /reset-password#token=... . The fragment is never sent
 * to the server, so the token stays out of proxy and app logs.
 *
 * Reading and stripping are deliberately separate steps. React is allowed to
 * run a state initialiser more than once (StrictMode in development does, on
 * every first mount), so an initialiser that also stripped the fragment would
 * find it already gone on the second run and lose the token.
 */
function readTokenFromHash(): string | null {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  return new URLSearchParams(hash).get('token');
}

export function ResetPasswordPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [token] = useState<string | null>(readTokenFromHash);

  // Strip the token from the address bar once it is in state, so it is not
  // left in browser history, a screenshot or a shared screen. Through the
  // router rather than history.replaceState, which would leave the router
  // still reporting the old fragment. Idempotent, so a second run is harmless.
  useEffect(() => {
    if (window.location.hash) {
      navigate({ pathname: window.location.pathname, search: window.location.search }, { replace: true });
    }
  }, [navigate]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const mutation = useMutation<void, ApiClientError, ResetPasswordRequest>({
    mutationFn: (body) => api.post<void>('/auth/reset-password', body),
    retry: false,
  });

  // The server revoked every session for this account, including any on this
  // browser. Drop the cached one so the app does not act signed in.
  useEffect(() => {
    if (mutation.isSuccess) queryClient.removeQueries({ queryKey: qk.session() });
  }, [mutation.isSuccess, queryClient]);

  const toLogin = (
    <Link to="/login" className="font-medium text-accent underline-offset-4 hover:underline">
      Go to sign in
    </Link>
  );

  if (!token) {
    return (
      <AuthLayout title="This link is not complete" subtitle="Reset your password" footer={toLogin}>
        <div className="space-y-3 text-sm text-ink">
          <p>
            The reset link is missing a part. This can happen if the link was cut off when it was
            copied, or if it was already opened once on this device.
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex min-h-11 items-center font-medium text-accent underline-offset-4 hover:underline"
          >
            Ask for a new link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (mutation.isSuccess) {
    return (
      <AuthLayout title="Password changed" subtitle="You can sign in now" footer={null}>
        <div className="space-y-4 text-sm text-ink">
          <p className="flex items-start gap-2">
            <CheckCircle2 aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-status-taken" />
            <span>
              Your new password is saved. For your safety, you have been signed out everywhere,
              including on other phones and computers.
            </span>
          </p>
          <Link
            to="/login"
            replace
            className="inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-accent px-4 font-medium text-accent-ink"
          >
            Sign in with your new password
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const fields = mutation.error?.fields;
  // Any failure that is not about the new password itself is about the link
  // (expired, used, or cut short so it failed validation) and must be shown.
  // Treating a validation error on `token` as "field-level" would hide it,
  // because there is no token field on this page to show it under.
  const generalError =
    mutation.error && !(mutation.error.isValidation && fields?.newPassword) ? mutation.error : null;
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (mutation.isPending || tooShort || mismatch || password.length === 0) return;
    mutation.mutate({ token, newPassword: password });
  };

  return (
    <AuthLayout title="Choose a new password" subtitle="Reset your password" footer={toLogin}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {generalError ? (
          <div className="space-y-2">
            <InlineError error={generalError} />
            <Link
              to="/forgot-password"
              className="inline-flex min-h-11 items-center text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Ask for a new link
            </Link>
          </div>
        ) : null}

        <PasswordField
          name="new-password"
          label="New password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fields?.newPassword ?? (tooShort ? `Use at least ${PASSWORD_MIN_LENGTH} characters.` : undefined)}
          autoComplete="new-password"
          showMinLengthHint
          required
          autoFocus
        />
        <PasswordField
          name="confirm-password"
          label="Type it again"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          error={mismatch ? 'The two passwords do not match.' : undefined}
          autoComplete="new-password"
          required
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={mutation.isPending}
          disabled={password.length === 0 || tooShort || mismatch || confirm.length === 0}
          className="w-full"
        >
          Save new password
        </Button>
      </form>
    </AuthLayout>
  );
}

export default ResetPasswordPage;
